import { combineSlices } from '@reduxjs/toolkit';

import { dfApi } from '@services/df/baseApi';

import { appReducer } from '@modules/app/slice';
import { authReducer } from '@modules/auth/slice';
import { clientReducer } from '@modules/client/slice';
import { deliveryRatesSlice } from '@modules/delivery-rates/slice';
import { inventoryPageSlice } from '@modules/inventory-page/slice';
import { invoiceSlice } from '@modules/invoice/slice';
import { viewerSlice } from '@modules/model-viewer/slice';
import { modelsReducer } from '@modules/models/slice';
import { modelsPageSlice } from '@modules/models-page/slice';
import { ordersSlice } from '@modules/orders/slice';
import { postProductionPricingSlice } from '@modules/post-production/slice';
import { priceCorrectionsSlice } from '@modules/price-corrections/slice';
import { uiReducer } from '@modules/ui';
import { uploadModelsReducer } from '@modules/upload-models/slice';
import { orderReducer } from '@modules/order/slice';
import { orderPageSlice } from '@modules/order-page/slice';
import { pricingSlice } from '@modules/pricing/slice';
import { productSlice } from '@modules/product/slice';
import { wallThicknessSlice } from '@modules/wall-thickness/slice';
import { quotationSlice } from '@modules/quotation/slice';
import { preselectionSlice } from '@modules/preselection/slice';
import { suitableMaterialsSlice } from '@modules/suitable-materials/slice';
import { technologiesSlice } from '@entities/technology/model';

/*
    Most often, queries are enough to solve a problem, but if there is logic relating to different components,
    complex async flow or you need data persistence at the browser level, then you can create a slice.

    Root reducer structure:

        export const rootReducer = combineSlices({
            domain/namespace: combineReducers({
                namespaceFeature1: reducer,
                namespaceFeature2: reducer,
                ...
            }),
            bigFeature: combineReducers({
                bigFeaturePart1: reducer,
                bigFeaturePart2: reducer,
                ...
            }),
            generic/uniqueFeature: reducer,
            ...
        });

    Maximum nesting depth is 2, if you need more, try to modularize reducer:
        1) https://www.reddit.com/r/reactjs/comments/166dl4x/comment/jyjfa8e/?utm_source=share&utm_medium=web3x&utm_name=web3xcss&utm_term=1&utm_content=share_button

    Slice naming convention:
        1) for single reducer 'uniqueFeature'
        2) for nested 'namespace/reducer'
 */

export const rootReducer = combineSlices({
    app: appReducer,
    auth: authReducer,
    client: clientReducer,
    deliveryRates: deliveryRatesSlice.reducer,
    modelViewer: viewerSlice.reducer,
    models: modelsReducer,
    modelsPage: modelsPageSlice.reducer,
    inventoryPage: inventoryPageSlice.reducer,
    invoice: invoiceSlice.reducer,
    order: orderReducer,
    orderPage: orderPageSlice.reducer,
    orders: ordersSlice.reducer,
    postProductionPricing: postProductionPricingSlice.reducer,
    product: productSlice.reducer,
    pricing: pricingSlice.reducer,
    priceCorrections: priceCorrectionsSlice.reducer,
    preselection: preselectionSlice.reducer,
    quotation: quotationSlice.reducer,
    technologies: technologiesSlice.reducer,
    suitableMaterials: suitableMaterialsSlice.reducer,
    uploadModels: uploadModelsReducer,
    ui: uiReducer,
    wallThickness: wallThicknessSlice.reducer,
    [dfApi.reducerPath]: dfApi.reducer,
});
